import React, { useState, useEffect, memo } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../@style/fiyat_tablo.css';
import upIcon from '../assets/icons/up.png';
import downIcon from '../assets/icons/down.png';

const WS_URL_HOME = 'wss://sslsocket.aifanet.com/5.250.255.84/13698';
const WS_URL_MILYEM = 'wss://sslsocket.aifanet.com/5.250.255.84/13700';

const SARRAFIYE_MILYEM_CATEGORY = 'SARRAFIYE MILYEM';
const GRAM_ALTIN_MILYEM_CATEGORY = 'GRAM ALTIN MILYEM';

const isValidPriceItem = (item) => {
  if (!item) return false;
  const ask = item.askprice !== null && item.askprice !== undefined
    ? item.askprice
    : (item.Ask !== null && item.Ask !== undefined ? item.Ask : null);
  const bid = item.bidprice !== null && item.bidprice !== undefined
    ? item.bidprice
    : (item.Bid !== null && item.Bid !== undefined ? item.Bid : null);
  return ask !== null && ask !== undefined && bid !== null && bid !== undefined;
};

const FiyatTablosu = memo(({ onSymbolSelect, mode = 'home' }) => {
  const wsUrl = mode === 'milyem' ? WS_URL_MILYEM : WS_URL_HOME;
  const { data } = useWebSocket(wsUrl);  
  // WebSocket'ten gelen verileri kategori olarak ayır
  const [goldTableData, setGoldTableData] = useState([]);
  const [currencyBureauTableData, setCurrencyBureauTableData] = useState([]);
  const [sarrafiyeMilyemTableData, setSarrafiyeMilyemTableData] = useState([]);
  const [gramAltinMilyemTableData, setGramAltinMilyemTableData] = useState([]);
  
  // Tüm verileri kayan yazı için sakla
  const [allData, setAllData] = useState([]);
  
  // Önceki fiyatları saklamak için state
  const [previousPrices, setPreviousPrices] = useState({});
  
  // Animasyon kontrolü için state
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Mobil cihaz kontrolü
  const [isMobile, setIsMobile] = useState(false);
  
  // Tema context'ini kullan
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Mobil cihaz kontrolü
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fiyat değerlerini al - hem askprice/bidprice hem de Ask/Bid formatlarını destekle
  const getAskPrice = (item) => {
    return item.askprice !== null && item.askprice !== undefined 
      ? item.askprice 
      : (item.Ask !== null && item.Ask !== undefined ? item.Ask : null);
  };

  const getBidPrice = (item) => {
    return item.bidprice !== null && item.bidprice !== undefined 
      ? item.bidprice 
      : (item.Bid !== null && item.Bid !== undefined ? item.Bid : null);
  };

  useEffect(() => {
    if (!data || !Array.isArray(data)) return;

    if (mode === 'milyem') {
      const sarrafiyeMilyemCategory = data.filter(
        item => item?.Category === SARRAFIYE_MILYEM_CATEGORY && isValidPriceItem(item)
      );
      const gramAltinMilyemCategory = data.filter(
        item => item?.Category === GRAM_ALTIN_MILYEM_CATEGORY && isValidPriceItem(item)
      );
      const allCategoriesData = [...sarrafiyeMilyemCategory, ...gramAltinMilyemCategory];

      setPreviousPrices(prevPrices => {
        const newPreviousPrices = { ...prevPrices };
        allCategoriesData.forEach(item => {
          const key = item.Code;
          const ask = getAskPrice(item);
          const bid = getBidPrice(item);
          if (!newPreviousPrices[key]) {
            newPreviousPrices[key] = { bid, ask };
          } else {
            newPreviousPrices[key] = {
              bid: newPreviousPrices[key].bid,
              ask: newPreviousPrices[key].ask
            };
          }
        });
        return newPreviousPrices;
      });

      setSarrafiyeMilyemTableData(sarrafiyeMilyemCategory);
      setGramAltinMilyemTableData(gramAltinMilyemCategory);
      setAllData(allCategoriesData);
    } else {
      const goldCategory = data.filter(item => item?.Category === 'ذهب' && isValidPriceItem(item));
      const currencyBureauCategory = data.filter(item => item?.Category === 'الصرافية' && isValidPriceItem(item));
      const currenciesCategory = data.filter(item => item?.Category === 'العملات' && isValidPriceItem(item));
      const allCategoriesData = [...goldCategory, ...currencyBureauCategory, ...currenciesCategory];

      setPreviousPrices(prevPrices => {
        const newPreviousPrices = { ...prevPrices };
        allCategoriesData.forEach(item => {
          const key = item.Code;
          const ask = getAskPrice(item);
          const bid = getBidPrice(item);
          if (!newPreviousPrices[key]) {
            newPreviousPrices[key] = { bid, ask };
          } else {
            newPreviousPrices[key] = {
              bid: newPreviousPrices[key].bid,
              ask: newPreviousPrices[key].ask
            };
          }
        });
        return newPreviousPrices;
      });

      setGoldTableData(goldCategory);
      setCurrencyBureauTableData(currencyBureauCategory);
      setAllData(allCategoriesData);
    }

    if (!isDataLoaded) {
      setIsDataLoaded(true);
    }
  }, [data, isDataLoaded, mode]);

  const getChangeColor = (change) => {
    if (change === null || change === undefined || isNaN(change)) return 'neutral';
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  const getTrendIcon = (change) => {
    if (change === null || change === undefined || isNaN(change) || change === 0) return null;
    if (change > 0) return upIcon;
    if (change < 0) return downIcon;
    return null;
  };

  const getPriceChange = (currentPrice, previousPrice) => {
    if (!currentPrice || !previousPrice || 
        currentPrice === null || previousPrice === null ||
        isNaN(currentPrice) || isNaN(previousPrice)) return 0;
    return currentPrice - previousPrice;
  };

  const getPriceColor = (currentPrice, previousPrice) => {
    const change = getPriceChange(currentPrice, previousPrice);
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  // Ask fiyatına göre yüzde değişimi hesapla
  const calculateAskPercentageChange = (currentAsk, previousAsk) => {
    if (!currentAsk || !previousAsk || 
        currentAsk === null || previousAsk === null ||
        currentAsk === 0 || previousAsk === 0 ||
        isNaN(currentAsk) || isNaN(previousAsk)) return 0;
    return ((currentAsk - previousAsk) / previousAsk) * 100;
  };

  // Fiyat formatını düzenle: 5.940,426 -> 5.940, 42,734 -> 42,734
  const formatPrice = (price) => {
    if (!price || price === null || isNaN(price)) return 'N/A';
    
    // Türkçe locale ile formatla
    const formatted = price.toLocaleString('tr-TR');
    
    // Eğer hem nokta (binlik ayırıcı) hem de virgül (ondalık ayırıcı) varsa
    // ondalık kısmı kaldır (örn: 5.940,426 -> 5.940)
    if (formatted.includes('.') && formatted.includes(',')) {
      return formatted.split(',')[0];
    }
    
    // Aksi halde olduğu gibi göster (örn: 42,734 -> 42,734)
    return formatted;
  };

  // Parite seçimi için handler
  const handleSymbolClick = (symbol) => {
    if (onSymbolSelect) {
      onSymbolSelect(symbol);
    }
  };

  // Mobil için dokunma geri bildirimi
  const handleTouchStart = (e) => {
    if (isMobile) {
      e.currentTarget.style.transform = 'scale(0.98)';
    }
  };

  const handleTouchEnd = (e) => {
    if (isMobile) {
      e.currentTarget.style.transform = 'scale(1)';
    }
  };

  // Tablo render fonksiyonu - görseldeki tasarımı kullanarak
  const renderTable = (tableData, tableIndex, categoryName) => {
    return (
      <div className={`price-panel ${isDataLoaded ? 'animate' : ''}`}>
        <div className="panel-header">{categoryName}</div>
        
        <div className="panel-content">
          <div className="table-header-row">
            <div>Birim</div>
            <div>Alış</div>
            <div>Satış</div>
          </div>
          
          {tableData.length > 0 ? (
            tableData.map((item, index) => {
              const ask = getAskPrice(item);
              const bid = getBidPrice(item);
              if (!item || !ask || !bid) return null;
              
              const previousBid = previousPrices[item.Code]?.bid || bid;
              const previousAsk = previousPrices[item.Code]?.ask || ask;
              
              const bidChange = getPriceChange(bid, previousBid);
              const askChange = getPriceChange(ask, previousAsk);
              
              return (
                <div 
                  key={`${tableIndex}-${index}`} 
                  className={`panel-row ${isDataLoaded ? 'animate' : ''}`}
                  onClick={() => handleSymbolClick(item.Code)}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  title={isMobile ? `${item.Code} seç` : `${item.Code} grafiğini açmak için tıklayın`}
                >
                  <div className="col-birim">
                    <div className="birim-code">{item.Code || 'N/A'}</div>
                    <div className="birim-desc">{item.Description || item.Code || 'N/A'}</div>
                  </div>
                  <div className={`col-alis ${getPriceColor(bid, previousBid)}`}>
                    {bidChange !== 0 && (
                      <img 
                        src={getTrendIcon(bidChange)} 
                        alt={bidChange > 0 ? t('home.increase') : t('home.decrease')}
                        className="trend-icon"
                      />
                    )}
                    <span className="price-value">
                      {bid ? formatPrice(bid) : '-'}
                    </span>
                  </div>
                  <div className={`col-satis ${getPriceColor(ask, previousAsk)}`}>
                    {askChange !== 0 && (
                      <img 
                        src={getTrendIcon(askChange)} 
                        alt={askChange > 0 ? t('home.increase') : t('home.decrease')}
                        className="trend-icon"
                      />
                    )}
                    <span className="price-value">
                      {ask ? formatPrice(ask) : '-'}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-data">
              <p>WebSocket'ten veri bekleniyor...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`fiyat-tablosu ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="tables-grid">
        {mode === 'milyem' ? (
          <>
            {renderTable(sarrafiyeMilyemTableData, 1, t('home.category.sarrafiye_milyem'))}
            {renderTable(gramAltinMilyemTableData, 2, t('home.category.gram_altin_milyem'))}
          </>
        ) : (
          <>
            {renderTable(goldTableData, 1, t('home.category.gold'))}
            {renderTable(currencyBureauTableData, 2, t('home.category.currency_bureau'))}
          </>
        )}
      </div>

      {/* Kayan Yazı - Mobilde gizle */}
      {!isMobile && (
        <div className="ticker-container">
          <div className="ticker-content">
            {allData.map((item, index) => {
              const ask = getAskPrice(item);
              const previousAsk = previousPrices[item.Code]?.ask || ask;
              const askPercentageChange = calculateAskPercentageChange(ask, previousAsk);
              
              return (
                <div key={index} className="ticker-item">
                  <span className="ticker-symbol">{item.Code}</span>
                  <span className={`ticker-price ${getChangeColor(askPercentageChange)}`}>
                    {formatPrice(ask)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

FiyatTablosu.displayName = 'FiyatTablosu';

export default FiyatTablosu;
